export type FormulaParseResult =
  | { valid: true; references: number[] }
  | { valid: false; references: number[]; error: string };

export type FormulaReferenceRange = {
  position: number;
  min: number;
  max: number;
};

export type FormulaOutputRangeResult =
  | { valid: true; references: number[]; min: number; max: number }
  | { valid: false; references: number[]; error: string };

type ParseFormulaOptions = {
  referenceablePositions?: number[];
};

type ExpressionNode =
  | { type: "number"; value: number }
  | { type: "reference"; position: number }
  | { type: "unary"; operator: "-"; value: ExpressionNode }
  | {
      type: "binary";
      operator: "+" | "-" | "*" | "/";
      left: ExpressionNode;
      right: ExpressionNode;
    };

type ParsedFormula = {
  references: number[];
  expression: ExpressionNode;
};

type NumericRange = {
  min: number;
  max: number;
};

const getConstantValue = (expression: ExpressionNode): number | undefined => {
  if (expression.type === "number") return expression.value;
  if (expression.type === "reference") return undefined;
  if (expression.type === "unary") {
    const value = getConstantValue(expression.value);
    return value === undefined ? undefined : -value;
  }

  const left = getConstantValue(expression.left);
  const right = getConstantValue(expression.right);
  if (left === undefined || right === undefined) return undefined;

  if (expression.operator === "+") return left + right;
  if (expression.operator === "-") return left - right;
  if (expression.operator === "*") return left * right;
  return right === 0 ? undefined : left / right;
};

type Token =
  | { type: "number"; value: string }
  | { type: "reference"; value: string; position: number }
  | { type: "operator"; value: "+" | "-" | "*" | "/" }
  | { type: "left-parenthesis"; value: "(" }
  | { type: "right-parenthesis"; value: ")" }
  | { type: "end"; value: "" };

class FormulaError extends Error {}

export const normalizeFormulaQuestionReferences = (formula: string) =>
  formula.replace(/\bq(\d+)/g, "Q$1");

const tokenize = (formula: string): Token[] => {
  const tokens: Token[] = [];
  let index = 0;

  while (index < formula.length) {
    const character = formula[index];

    if (/\s/.test(character)) {
      index += 1;
      continue;
    }

    if (/\d/.test(character)) {
      const start = index;
      while (index < formula.length && /\d/.test(formula[index])) index += 1;
      if (formula[index] === ".") {
        throw new FormulaError("Decimal constants are not supported; use integers.");
      }
      tokens.push({ type: "number", value: formula.slice(start, index) });
      continue;
    }

    if (character === "Q" || character === "q") {
      const start = index;
      index += 1;
      const digitStart = index;
      while (index < formula.length && /\d/.test(formula[index])) index += 1;
      if (digitStart === index) {
        throw new FormulaError(
          "Question references need a position, for example Q1.",
        );
      }
      const value = formula.slice(start, index).replace(/^q/, "Q");
      tokens.push({ type: "reference", value, position: Number(value.slice(1)) });
      continue;
    }

    if (character === "+" || character === "-" || character === "*" || character === "/") {
      tokens.push({ type: "operator", value: character });
      index += 1;
      continue;
    }

    if (character === "(") {
      tokens.push({ type: "left-parenthesis", value: character });
      index += 1;
      continue;
    }

    if (character === ")") {
      tokens.push({ type: "right-parenthesis", value: character });
      index += 1;
      continue;
    }

    throw new FormulaError(
      `Unsupported token "${character}". Use Q references, integers, parentheses, and + - * /.`,
    );
  }

  tokens.push({ type: "end", value: "" });
  return tokens;
};

class FormulaParser {
  private index = 0;
  private readonly references = new Set<number>();

  constructor(
    private readonly tokens: Token[],
    private readonly questionCount: number,
    private readonly referenceablePositions?: Set<number>,
  ) {}

  parse(): ParsedFormula {
    const expression = this.parseExpression();
    const token = this.current();
    if (token.type === "right-parenthesis") {
      throw new FormulaError("There is a closing parenthesis without a matching opening parenthesis.");
    }
    if (token.type !== "end") {
      throw new FormulaError(`Expected an arithmetic operator before "${token.value}".`);
    }
    return {
      references: Array.from(this.references).sort((a, b) => a - b),
      expression,
    };
  }

  private current() {
    return this.tokens[this.index];
  }

  private advance() {
    this.index += 1;
  }

  private parseExpression(): ExpressionNode {
    let value = this.parseTerm();
    while (
      this.current().type === "operator" &&
      (this.current().value === "+" || this.current().value === "-")
    ) {
      const operator = this.current().value as "+" | "-";
      this.advance();
      const right = this.parseTerm();
      value = { type: "binary", operator, left: value, right };
    }
    return value;
  }

  private parseTerm(): ExpressionNode {
    let value = this.parseUnary();
    while (
      this.current().type === "operator" &&
      (this.current().value === "*" || this.current().value === "/")
    ) {
      const operator = this.current().value as "*" | "/";
      this.advance();
      const right = this.parseUnary();
      if (operator === "/" && getConstantValue(right) === 0) {
        throw new FormulaError("Division by zero is not allowed.");
      }
      value = { type: "binary", operator, left: value, right };
    }
    return value;
  }

  private parseUnary(): ExpressionNode {
    if (this.current().type === "operator" && this.current().value === "-") {
      this.advance();
      const value = this.parseUnary();
      return { type: "unary", operator: "-", value };
    }
    return this.parsePrimary();
  }

  private parsePrimary(): ExpressionNode {
    const token = this.current();

    if (token.type === "number") {
      const value = Number(token.value);
      if (!Number.isFinite(value)) {
        throw new FormulaError("Integer constants must be finite numbers.");
      }
      this.advance();
      return { type: "number", value };
    }

    if (token.type === "reference") {
      if (token.position === 0) {
        throw new FormulaError("Q0 is not valid; question positions start at Q1.");
      }
      if (token.value.length > 2 && token.value[1] === "0") {
        throw new FormulaError(
          `${token.value} is not valid; question references cannot use leading zeros.`,
        );
      }
      if (token.position > this.questionCount) {
        throw new FormulaError(
          `${token.value} does not exist. This quiz currently has ${this.questionCount} question${
            this.questionCount === 1 ? "" : "s"
          }.`,
        );
      }
      if (
        this.referenceablePositions &&
        !this.referenceablePositions.has(token.position)
      ) {
        throw new FormulaError(
          `${token.value} references a text question. Text questions cannot be used in interpretation formulas.`,
        );
      }
      this.references.add(token.position);
      this.advance();
      return { type: "reference", position: token.position };
    }

    if (token.type === "left-parenthesis") {
      this.advance();
      const value = this.parseExpression();
      if (this.current().type !== "right-parenthesis") {
        throw new FormulaError("An opening parenthesis is missing its closing parenthesis.");
      }
      this.advance();
      return value;
    }

    if (token.type === "end") {
      throw new FormulaError("The formula ends before the expression is complete.");
    }

    if (token.type === "right-parenthesis") {
      throw new FormulaError("A value is required before the closing parenthesis.");
    }

    throw new FormulaError(`A value is required after "${token.value}".`);
  }
}

const parseFormula = (
  formula: string,
  questionCount: number,
  options: ParseFormulaOptions = {},
) => {
  const parser = new FormulaParser(
    tokenize(formula),
    questionCount,
    options.referenceablePositions
      ? new Set(options.referenceablePositions)
      : undefined,
  );
  return parser.parse();
};

const assertFiniteRange = (range: NumericRange) => {
  if (!Number.isFinite(range.min) || !Number.isFinite(range.max)) {
    throw new FormulaError("The formula output range could not be determined.");
  }
  return range;
};

const multiplyRanges = (left: NumericRange, right: NumericRange) => {
  const values = [
    left.min * right.min,
    left.min * right.max,
    left.max * right.min,
    left.max * right.max,
  ];
  return assertFiniteRange({
    min: Math.min(...values),
    max: Math.max(...values),
  });
};

const divideRanges = (left: NumericRange, right: NumericRange) => {
  if (right.min <= 0 && right.max >= 0) {
    throw new FormulaError(
      "The formula can divide by zero for some valid answer values.",
    );
  }

  return multiplyRanges(left, {
    min: Math.min(1 / right.min, 1 / right.max),
    max: Math.max(1 / right.min, 1 / right.max),
  });
};

const evaluateRange = (
  expression: ExpressionNode,
  references: Map<number, NumericRange>,
): NumericRange => {
  if (expression.type === "number") {
    return { min: expression.value, max: expression.value };
  }

  if (expression.type === "reference") {
    const range = references.get(expression.position);
    if (!range) {
      throw new FormulaError(
        `Q${expression.position} does not have a finite answer range.`,
      );
    }
    return range;
  }

  if (expression.type === "unary") {
    const value = evaluateRange(expression.value, references);
    return { min: -value.max, max: -value.min };
  }

  const left = evaluateRange(expression.left, references);
  const right = evaluateRange(expression.right, references);

  if (expression.operator === "+") {
    return assertFiniteRange({
      min: left.min + right.min,
      max: left.max + right.max,
    });
  }

  if (expression.operator === "-") {
    return assertFiniteRange({
      min: left.min - right.max,
      max: left.max - right.min,
    });
  }

  if (expression.operator === "*") {
    return multiplyRanges(left, right);
  }

  return divideRanges(left, right);
};

export const parseInterpretationFormula = (
  formula: string,
  questionCount: number,
  options: ParseFormulaOptions = {},
): FormulaParseResult => {
  if (!formula.trim()) {
    return { valid: false, references: [], error: "Enter a formula." };
  }

  try {
    const parsed = parseFormula(formula, questionCount, options);
    return { valid: true, references: parsed.references };
  } catch (error) {
    return {
      valid: false,
      references: [],
      error:
        error instanceof FormulaError
          ? error.message
          : "The formula could not be parsed.",
    };
  }
};

export const getInterpretationFormulaOutputRange = (
  formula: string,
  questionCount: number,
  referenceRanges: FormulaReferenceRange[],
  options: ParseFormulaOptions = {},
): FormulaOutputRangeResult => {
  if (!formula.trim()) {
    return { valid: false, references: [], error: "Enter a formula." };
  }

  try {
    const parsed = parseFormula(formula, questionCount, options);
    const range = evaluateRange(
      parsed.expression,
      new Map(
        referenceRanges.map((referenceRange) => [
          referenceRange.position,
          { min: referenceRange.min, max: referenceRange.max },
        ]),
      ),
    );

    return {
      valid: true,
      references: parsed.references,
      min: Math.ceil(range.min),
      max: Math.ceil(range.max),
    };
  } catch (error) {
    return {
      valid: false,
      references: [],
      error:
        error instanceof FormulaError
          ? error.message
          : "The formula output range could not be determined.",
    };
  }
};
